import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import CurrencyInput from 'react-currency-input-field';
import { liveQuery } from 'dexie';
import db from '../../db/db';
import { setAlert } from '../../actions/alert';
import TicketCorte from './TicketCorte';

const currencyStyle = (focused, disabled) => ({
    background: disabled ? '#161616' : '#1F1F1F',
    border: `2px solid ${focused ? '#F5C800' : '#2E2E2E'}`,
    boxShadow: focused ? '0 0 0 2px rgba(245,200,0,0.15)' : 'none',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
    color: disabled ? '#555' : '#F0F0F0',
    fontSize: '1.1rem',
    fontWeight: 700,
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'text',
});

const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Corte = () => {
    const dispatch = useDispatch();

    const [empleadoLogueado, setEmpleadoLogueado] = useState(null);
    const [metodos, setMetodos]           = useState([]);
    const [totalInicial, setTotalInicial] = useState('');
    const [focused, setFocused]           = useState(false);
    const [conosInicial, setConosInicial] = useState('');
    const [focusedConos, setFocusedConos] = useState(false);
    const [conosFinal, setConosFinal]     = useState('');
    const [focusedConosFinal, setFocusedConosFinal] = useState(false);
    const [observacionesConos, setObservacionesConos] = useState('');
    const [totalesMetodos, setTotalesMetodos]           = useState({});
    const [observacionesMetodos, setObservacionesMetodos] = useState('');
    const [focusedMetodo, setFocusedMetodo]             = useState(null);
    const [corteActivo, setCorteActivo]   = useState(null);
    const [eventoActivoRef, setEventoActivoRef] = useState(null);
    const [sucursalNombre, setSucursalNombre] = useState('');
    const [statusBtn, setStatusBtn]           = useState(false);
    const [mostrarTicketCorte, setMostrarTicketCorte] = useState(false);
    const [recienFinalizado, setRecienFinalizado]     = useState(false);

    // Datos del día
    const [ventas, setVentas]             = useState([]);
    const [vueltas, setVueltas]           = useState([]);
    const [pagosVueltas, setPagosVueltas] = useState([]);
    const [gastos, setGastos]             = useState([]);
    const [conos, setConos]               = useState([]);

    // Desglose por producto
    const [productosAlimentos, setProductosAlimentos] = useState([]);
    const [empleadosSucursal, setEmpleadosSucursal]   = useState([]);
    const [ventasProductosAll, setVentasProductosAll] = useState([]);
    const [vueltasProductosAll, setVueltasProductosAll] = useState([]);

    // ── Carga / recarga de datos ─────────────────────────────────────────────
    const cargarDatos = async () => {
        const sesion = await db.sesion.toArray();
        const logueado = sesion[0];
        setEmpleadoLogueado(logueado);
        const sucursalId = logueado.sucursal?._id || logueado.sucursal;

        // Buscar el corte más reciente pendiente (status: false) de esta sucursal
        const todosCortes = await db.cortes.toArray();
        const cortePendiente = todosCortes
            .filter(c => c.status === false && String(c.sucursal?._id || c.sucursal) === String(sucursalId))
            .sort((a, b) => new Date(b.fecha_inicio || b.fecha) - new Date(a.fecha_inicio || a.fecha))[0] || null;

        const sucursalReg = await db.sucursales
            .filter(s => String(s._id || s.id) === String(sucursalId))
            .first();
        const eventoActivo = sucursalReg?.evento || null;

        const [mets, vs, vls, pgs, gs, prods, emps, vProds, vlProds, conosList] = await Promise.all([
            db.metodos.toArray(),
            db.ventas.filter(v => !v.corte_local_id && String(v.sucursal?._id || v.sucursal) === String(sucursalId)).toArray(),
            db.vueltas.filter(v => !v.corte_local_id && !v.cancelada && String(v.sucursal?._id || v.sucursal) === String(sucursalId)).toArray(),
            db.pagos_vueltas.filter(p => !p.corte_local_id && String(p.sucursal?._id || p.sucursal) === String(sucursalId)).toArray(),
            db.gastos.filter(g => !g.corte_local_id && String(g.sucursal?._id || g.sucursal) === String(sucursalId)).toArray(),
            db.productos.filter(p => p.tipo === 0).toArray(),
            db.empleados.filter(e => {
                const ids = Array.isArray(e.sucursales) && e.sucursales.length > 0
                    ? e.sucursales.map(s => String(s?._id || s))
                    : [String(e.sucursal?._id || e.sucursal)];
                return ids.includes(String(sucursalId)) && e.tipo !== 5;
            }).toArray(),
            db.ventas_productos.toArray(),
            db.vueltas_productos.toArray(),
            db.conos.toArray(),
        ]);

        const ventasActivas = vs.filter(v => v.status === true);
        const ventaIdSet    = new Set(ventasActivas.map(v => v.id));
        const vueltaIdSet   = new Set(vls.map(v => v.id));

        setMetodos(mets);
        setVentas(ventasActivas);
        setVueltas(vls);
        setPagosVueltas(pgs);
        setGastos(gs);
        setProductosAlimentos(prods);
        setEmpleadosSucursal(emps);
        setVentasProductosAll(vProds.filter(vp => ventaIdSet.has(vp.venta_local_id)));
        setVueltasProductosAll(vlProds.filter(vp => vueltaIdSet.has(vp.vuelta_local_id)));
        setConos(conosList.filter(c => !c.corte_local_id));

        // Solo cargar el corte si está pendiente; si no, limpiar estado
        if (cortePendiente) {
            setCorteActivo(cortePendiente);
            setTotalInicial(String(cortePendiente.total_inicial || ''));
            setConosInicial(String(cortePendiente.conos_inicial ?? ''));
            setConosFinal(String(cortePendiente.conos_final ?? ''));
            setObservacionesConos(cortePendiente.observaciones_conos || '');
            const totMap = {};
            (cortePendiente.totales || []).forEach(t => { totMap[t.metodo] = String(t.total || ''); });
            setTotalesMetodos(totMap);
            setObservacionesMetodos(cortePendiente.observaciones_metodos || '');
        } else {
            setCorteActivo(null);
            setTotalInicial('');
            setConosInicial('');
            setConosFinal('');
            setObservacionesConos('');
            setTotalesMetodos({});
            setObservacionesMetodos('');
        }

        setSucursalNombre(sucursalReg?.nombre || '');
        setEventoActivoRef(eventoActivo);
    };

    useEffect(() => { cargarDatos(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Conos en tiempo real ──────────────────────────────────────────────────
    useEffect(() => {
        const subscription = liveQuery(() => db.conos.toArray()).subscribe({
            next:  result => setConos(result.filter(c => !c.corte_local_id)),
            error: err    => console.error('liveQuery conos:', err),
        });
        return () => subscription.unsubscribe();
    }, []);

    // ── Totales derivados ─────────────────────────────────────────────────────
    const totalVentas   = ventas.reduce((a, v) => a + (v.total || 0), 0);
    const totalVueltas  = vueltas.reduce((a, v) => a + (v.total || 0), 0);
    const totalGastos   = gastos.reduce((a, g) => a + (g.total || 0), 0);
    const finalizado    = corteActivo?.status === true;
    const pendiente     = corteActivo && !finalizado;

    const totalInicialf = parseFloat(totalInicial) || 0;
    const totalFinal    = totalInicialf + totalVentas + totalVueltas - totalGastos;

    const esEfectivo = (m) => m.nombre?.toLowerCase().includes('efectivo');

    const desgloseMetodos = metodos.map(m => {
        const idMet = m._id;
        const porVentas   = ventas.filter(v => v.metodo === idMet).reduce((a, v) => a + (v.total || 0), 0);
        const porVueltas  = pagosVueltas.filter(p => p.metodo === idMet).reduce((a, p) => a + (p.total || 0), 0);
        const porGastos   = gastos.filter(g => g.metodo === idMet).reduce((a, g) => a + (g.total || 0), 0);
        const inicialExtra = esEfectivo(m) ? totalInicialf : 0;
        return { _id: m._id, nombre: m.nombre, porVentas, porVueltas, porGastos, inicialExtra, neto: inicialExtra + porVentas + porVueltas - porGastos };
    }).filter(m => m.porVentas > 0 || m.porVueltas > 0 || m.porGastos > 0 || m.inicialExtra > 0);

    const generarFolio = () => {
        const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        return letras[Math.floor(Math.random() * 26)] +
               letras[Math.floor(Math.random() * 26)] +
               Math.floor(Math.random() * 900 + 100);
    };

    // ── Iniciar corte (status: false) ─────────────────────────────────────────
    const handleIniciar = async () => {
        if (!totalInicial || parseFloat(totalInicial) < 0)
            return dispatch(setAlert('Ingresa el efectivo inicial', 'danger'));
        if (!conosInicial || parseInt(conosInicial) < 0)
            return dispatch(setAlert('Ingresa los conos iniciales', 'danger'));

        // Guardia: verificar que no exista ya un corte pendiente
        const cortePendiente = await db.cortes.filter(c => c.status === false).first();
        if (cortePendiente) {
            setCorteActivo(cortePendiente);
            setTotalInicial(String(cortePendiente.total_inicial || ''));
            return dispatch(setAlert('Ya existe un corte pendiente sin finalizar', 'danger'));
        }

        setStatusBtn(true);
        try {
            const folio = generarFolio();
            const registro = {
                folio,
                sucursal:      empleadoLogueado?.sucursal?._id || empleadoLogueado?.sucursal,
                responsable:   empleadoLogueado?._id,
                evento:        eventoActivoRef?._id || null,
                total_inicial: parseFloat(totalInicial),
                conos_inicial: parseInt(conosInicial) || 0,
                fecha_inicio:  new Date(),
                status:        false,
                sincronizado:  false,
                ticket_impreso: false,
            };
            const newId = await db.cortes.add(registro);
            setCorteActivo({ ...registro, id: newId });
            setRecienFinalizado(false);
            setConosFinal('');
            setObservacionesConos('');
            dispatch(setAlert(`Corte ${folio} iniciado`, 'success'));
        } catch {
            dispatch(setAlert('Error al iniciar el corte', 'danger'));
        } finally {
            setStatusBtn(false);
        }
    };

    // ── Guardar iniciales (corte pendiente) ──────────────────────────────────
    const handleGuardar = async () => {
        if (!totalInicial || parseFloat(totalInicial) < 0)
            return dispatch(setAlert('Ingresa el efectivo inicial', 'danger'));
        if (!conosInicial || parseInt(conosInicial) < 0)
            return dispatch(setAlert('Ingresa los conos iniciales', 'danger'));

        setStatusBtn(true);
        try {
            const cambios = {
                total_inicial:          parseFloat(totalInicial),
                conos_inicial:          parseInt(conosInicial) || 0,
                conos_final:            conosFinal !== '' ? parseInt(conosFinal) : undefined,
                observaciones_conos:    observacionesConos || '',
                totales:                desgloseMetodos.map(m => ({ metodo: m._id, total: parseFloat(totalesMetodos[m._id]) || 0 })),
                observaciones_metodos:  observacionesMetodos,
            };
            await db.cortes.update(corteActivo.id, cambios);
            setCorteActivo(prev => ({ ...prev, ...cambios }));
            dispatch(setAlert('Datos guardados', 'success'));
        } catch {
            dispatch(setAlert('Error al guardar', 'danger'));
        } finally {
            setStatusBtn(false);
        }
    };

    // ── Finalizar corte (status: true) ───────────────────────────────────────
    const handleFinalizar = async () => {
        const revisionDelCorte = await db.revisiones
            .filter(r => r.corte_local_id === corteActivo?.id)
            .first();
        if (!revisionDelCorte)
            return dispatch(setAlert('Debes completar la revisión antes de finalizar el corte', 'danger'));

        const vueltasPendientes = vueltas.filter(v => v.status === false).length;
        if (vueltasPendientes > 0)
            return dispatch(setAlert(
                `Tienes ${vueltasPendientes} venta${vueltasPendientes > 1 ? 's' : ''} ambulante${vueltasPendientes > 1 ? 's' : ''} pendiente${vueltasPendientes > 1 ? 's' : ''} de finalizar`,
                'danger'
            ));
        if (conosFinal === '' || parseInt(conosFinal) < 0)
            return dispatch(setAlert('Ingresa la cantidad de conos finales', 'danger'));

        setStatusBtn(true);
        try {
            const cambios = {
                total_inicial:          parseFloat(totalInicial) || 0,
                conos_inicial:          parseInt(conosInicial) || 0,
                conos_final:            conosFinal !== '' ? parseInt(conosFinal) : null,
                observaciones_conos:    observacionesConos || '',
                totales:                desgloseMetodos.map(m => ({ metodo: m._id, total: parseFloat(totalesMetodos[m._id]) || 0 })),
                observaciones_metodos:  observacionesMetodos,
                total_ventas:           totalVentas,
                total_vueltas:          totalVueltas,
                total_gastos:           totalGastos,
                total_final:            totalFinal,
                desglose_metodos:       desgloseMetodos,
                total_conos_ingresados: totalConosIngresados,
                total_conos_vendidos:   totalVendidosGeneral,
                tipos_conos:            productosAlimentos.map((prod, i) => ({
                    nombre:   prod.nombre,
                    vendidos: vendidosPorProducto[i] || 0,
                })).filter(t => t.vendidos > 0),
                evento_nombre:          eventoActivoRef?.nombre || '',
                fecha_cierre:           new Date(),
                status:                 true,
            };
            await db.cortes.update(corteActivo.id, cambios);

            // Marcar ventas, vueltas, gastos, pagos_vueltas y conos como pertenecientes a este corte
            await Promise.all([
                ...ventas.map(v => db.ventas.update(v.id, { corte_local_id: corteActivo.id })),
                ...vueltas.map(v => db.vueltas.update(v.id, { corte_local_id: corteActivo.id })),
                ...gastos.map(g => db.gastos.update(g.id, { corte_local_id: corteActivo.id })),
                ...pagosVueltas.map(p => db.pagos_vueltas.update(p.id, { corte_local_id: corteActivo.id })),
                ...conos.map(c => db.conos.update(c.id, { corte_local_id: corteActivo.id })),
            ]);

            setCorteActivo(prev => ({ ...prev, ...cambios }));
            setRecienFinalizado(true);
            dispatch(setAlert(`Corte ${corteActivo.folio} finalizado correctamente`, 'success'));
        } catch {
            dispatch(setAlert('Error al finalizar el corte', 'danger'));
        } finally {
            setStatusBtn(false);
        }
    };

    // ── Desglose por producto (totales vendidos) ──────────────────────────────
    const ventasPorProducto = productosAlimentos.map(prod => {
        const prodId = prod._id || String(prod.id);
        return ventasProductosAll.filter(vp => String(vp.producto) === String(prodId)).reduce((a, vp) => a + (vp.cantidad || 0), 0);
    });
    const ambulantesPorProducto = productosAlimentos.map(prod => {
        const prodId = prod._id || String(prod.id);
        return vueltasProductosAll.filter(vp => String(vp.producto) === String(prodId)).reduce((a, vp) => a + ((vp.cantidad || 0) - (vp.cantidad_regresada || 0)), 0);
    });
    const vendidosPorProducto = productosAlimentos.map((_, i) => ventasPorProducto[i] + ambulantesPorProducto[i]);
    const totalVentasFila      = ventasPorProducto.reduce((a, b) => a + b, 0);
    const totalAmbulantesFila  = ambulantesPorProducto.reduce((a, b) => a + b, 0);
    const totalVendidosGeneral = vendidosPorProducto.reduce((a, b) => a + b, 0);
    const mostrarTablaVendidos = productosAlimentos.length > 0 && (ventasProductosAll.length > 0 || vueltasProductosAll.length > 0);

    // ── Conos ─────────────────────────────────────────────────────────────────
    const conosInicialNum      = parseInt(conosInicial) || 0;
    const totalConosIngresados = finalizado
        ? (corteActivo?.total_conos_ingresados || 0)
        : conos.reduce((a, c) => a + (c.cantidad || 0), 0);
    const conosVendidosDisplay = finalizado
        ? (corteActivo?.total_conos_vendidos || totalVendidosGeneral)
        : totalVendidosGeneral;
    const conosActuales        = conosInicialNum + totalConosIngresados - conosVendidosDisplay;

    // ── Desglose por empleado ─────────────────────────────────────────────────
    // Tipos 0,1,2,3: suman ventas donde aparecen + vueltas donde aparecen
    // Tipo 4: solo vueltas donde aparecen
    const TIPOS_VENTA = [0, 1, 2, 3];
    const matrizFilas = empleadosSucursal.map(emp => {
        const empId = String(emp._id);
        const esTipoVenta = TIPOS_VENTA.includes(emp.tipo);

        const celdas = productosAlimentos.map(prod => {
            const prodId = prod._id || String(prod.id);
            let total = 0;

            if (esTipoVenta) {
                total += ventasProductosAll
                    .filter(vp => {
                        if (String(vp.producto) !== String(prodId)) return false;
                        const venta = ventas.find(v => v.id === vp.venta_local_id);
                        return venta && (venta.empleados || []).some(ev => String(ev.empleado) === empId);
                    })
                    .reduce((a, vp) => a + (vp.cantidad || 0), 0);
            }

            // Todos los tipos (incluye tipo 4) suman vueltas donde aparecen
            total += vueltasProductosAll
                .filter(vp => {
                    if (String(vp.producto) !== String(prodId)) return false;
                    const vuelta = vueltas.find(v => v.id === vp.vuelta_local_id);
                    return vuelta && (vuelta.empleados || []).some(ev => String(ev.empleado) === empId);
                })
                .reduce((a, vp) => a + ((vp.cantidad || 0) - (vp.cantidad_regresada || 0)), 0);

            return total;
        });
        return { emp, celdas, total: celdas.reduce((a, b) => a + b, 0) };
    });
    const mostrarTablaEmpleados = productosAlimentos.length > 0;

    const fechaHoy     = new Date().toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <div style={styles.page}>

            {/* ── Encabezado ── */}
            <div style={styles.header}>
                <div>
                    <div style={styles.headerTitulo}>
                        <i className="fas fa-cash-register me-3" style={{ color: '#F5C800' }}></i>
                        Corte de caja
                        {corteActivo && (
                            <span style={{ ...styles.folioTag, marginLeft: '0.75rem' }}>{corteActivo.folio}</span>
                        )}
                        {corteActivo && (
                            <span style={{ ...styles.statusTag, ...(finalizado ? styles.statusFinalizado : styles.statusPendiente) }}>
                                {finalizado ? 'Finalizado' : 'Pendiente'}
                            </span>
                        )}
                    </div>
                    <div style={styles.headerFecha}>
                        {fechaHoy.charAt(0).toUpperCase() + fechaHoy.slice(1)}
                    </div>
                </div>
                {empleadoLogueado && (
                    <div style={styles.responsableBadge}>
                        <i className="fas fa-user-shield me-2" style={{ color: '#555' }}></i>
                        <span style={{ color: '#C0C0C0', fontSize: '0.9rem' }}>
                            {`${empleadoLogueado.nombre || ''} ${empleadoLogueado.apellido_paterno || ''}`.trim()}
                        </span>
                    </div>
                )}
            </div>

            {/* ── Efectivo inicial + Conos iniciales ── */}
            <div style={styles.inicialesGrid}>
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-wallet me-2" style={{ color: '#F5C800' }}></i>
                        Efectivo inicial
                    </div>
                    <div style={styles.totalInicialWrap}>
                        <CurrencyInput
                            value={totalInicial}
                            onValueChange={val => !pendiente && setTotalInicial(val || '')}
                            prefix="$"
                            decimalsLimit={2}
                            groupSeparator=","
                            decimalSeparator="."
                            placeholder="$0.00"
                            disabled={pendiente}
                            onFocus={() => setFocused(true)}
                            onBlur={() => setFocused(false)}
                            style={currencyStyle(focused, pendiente)}
                        />
                        <p style={styles.hint}>Cantidad en efectivo con la que inició el día</p>
                    </div>
                </div>

                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-ice-cream me-2" style={{ color: '#F5C800' }}></i>
                        Conos iniciales
                    </div>
                    <div style={styles.totalInicialWrap}>
                        <CurrencyInput
                            value={conosInicial}
                            onValueChange={val => !pendiente && setConosInicial(val || '')}
                            decimalsLimit={0}
                            groupSeparator=","
                            decimalSeparator="."
                            placeholder="0"
                            disabled={pendiente}
                            onFocus={() => setFocusedConos(true)}
                            onBlur={() => setFocusedConos(false)}
                            style={currencyStyle(focusedConos, pendiente)}
                        />
                        <p style={styles.hint}>Cantidad de conos con la que inició el día</p>
                    </div>
                </div>
            </div>

            {/* ── Rubros ── */}
            <div style={styles.rubrosGrid}>
                <div style={{ ...styles.rubroCard, borderTop: '3px solid #48BB78' }}>
                    <span style={styles.rubroLabel}>
                        <i className="fas fa-receipt me-2" style={{ color: '#48BB78' }}></i>Ventas
                    </span>
                    <span style={{ ...styles.rubroTotal, color: '#48BB78' }}>${fmt(totalVentas)}</span>
                    <span style={styles.rubroSub}>{ventas.length} {ventas.length === 1 ? 'venta' : 'ventas'} realizadas</span>
                </div>

                <div style={{ ...styles.rubroCard, borderTop: '3px solid #63B3ED' }}>
                    <span style={styles.rubroLabel}>
                        <i className="fas fa-rotate-left me-2" style={{ color: '#63B3ED' }}></i>V. ambulantes
                    </span>
                    <span style={{ ...styles.rubroTotal, color: '#63B3ED' }}>${fmt(totalVueltas)}</span>
                    <span style={styles.rubroSub}>{vueltas.length} {vueltas.length === 1 ? 'venta ambulante' : 'ventas ambulantes'} registradas</span>
                </div>

                <div style={{ ...styles.rubroCard, borderTop: '3px solid #ED8936' }}>
                    <span style={styles.rubroLabel}>
                        <i className="fas fa-file-invoice-dollar me-2" style={{ color: '#ED8936' }}></i>Gastos
                    </span>
                    <span style={{ ...styles.rubroTotal, color: '#ED8936' }}>-${fmt(totalGastos)}</span>
                    <span style={styles.rubroSub}>{gastos.length} {gastos.length === 1 ? 'gasto' : 'gastos'} registrados</span>
                </div>

                <div style={{ ...styles.rubroCard, borderTop: '3px solid #F5C800', background: 'rgba(245,200,0,0.05)' }}>
                    <span style={styles.rubroLabel}>
                        <i className="fas fa-equals me-2" style={{ color: '#F5C800' }}></i>Total final
                    </span>
                    <span style={{ ...styles.rubroTotal, color: '#F5C800', fontSize: '1.8rem' }}>${fmt(totalFinal)}</span>
                    <span style={styles.rubroSub}>Inicial + Ventas + V. ambulantes − Gastos</span>
                </div>
            </div>

            {/* ── Desglose por método ── */}
            {desgloseMetodos.length > 0 && (
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-credit-card me-2" style={{ color: '#F5C800' }}></i>
                        Desglose por método de pago
                    </div>
                    <div style={styles.tablaWrap}>
                        <table style={styles.tabla}>
                            <thead>
                                <tr>
                                    {['Método', 'Inicial', 'Ventas', 'V. amb.', 'Gastos', 'Neto'].map(col => (
                                        <th key={col} style={styles.th}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {desgloseMetodos.map((m, idx) => (
                                    <tr key={idx} style={{ background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                        <td style={styles.td}><span style={{ color: '#F0F0F0', fontWeight: 600 }}>{m.nombre}</span></td>
                                        <td style={styles.td}>
                                            {m.inicialExtra > 0
                                                ? <span style={{ color: '#F5C800', fontWeight: 600 }}>${fmt(m.inicialExtra)}</span>
                                                : <span style={{ color: '#333' }}>—</span>
                                            }
                                        </td>
                                        <td style={styles.td}><span style={{ color: '#48BB78', fontWeight: 600 }}>${fmt(m.porVentas)}</span></td>
                                        <td style={styles.td}><span style={{ color: '#63B3ED', fontWeight: 600 }}>${fmt(m.porVueltas)}</span></td>
                                        <td style={styles.td}><span style={{ color: '#ED8936', fontWeight: 600 }}>${fmt(m.porGastos)}</span></td>
                                        <td style={styles.td}>
                                            <span style={{ color: m.neto >= 0 ? '#F5C800' : '#E53E3E', fontWeight: 800 }}>
                                                ${fmt(m.neto)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Cantidades reales + observaciones */}
                    <div style={{ padding: '1.25rem', borderTop: '1px solid #2E2E2E', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <span style={styles.fieldLabel}>Cantidades reales por método</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                            {desgloseMetodos.map(m => {
                                const calculado = m.neto;
                                const real      = parseFloat(totalesMetodos[m._id]) || 0;
                                const diff      = real - calculado;
                                const hasReal   = totalesMetodos[m._id] !== undefined && totalesMetodos[m._id] !== '';
                                return (
                                    <div key={m._id} style={{ flex: '0 0 180px' }}>
                                        <label style={styles.fieldLabel}>{m.nombre}</label>
                                        <CurrencyInput
                                            value={totalesMetodos[m._id] || ''}
                                            onValueChange={val => !finalizado && setTotalesMetodos(prev => ({ ...prev, [m._id]: val || '' }))}
                                            prefix="$"
                                            decimalsLimit={2}
                                            groupSeparator=","
                                            decimalSeparator="."
                                            placeholder="$0.00"
                                            disabled={finalizado}
                                            onFocus={() => setFocusedMetodo(m._id)}
                                            onBlur={() => setFocusedMetodo(null)}
                                            style={currencyStyle(focusedMetodo === m._id, finalizado)}
                                        />
                                        <div style={styles.comparativaWrap}>
                                            <div style={styles.comparativaFila}>
                                                <span style={styles.comparativaLbl}>Calculado</span>
                                                <span style={styles.comparativaVal}>${fmt(calculado)}</span>
                                            </div>
                                            {hasReal && (
                                                <div style={{ ...styles.comparativaFila, marginTop: '0.2rem', paddingTop: '0.35rem', borderTop: '1px solid #2E2E2E' }}>
                                                    <span style={styles.comparativaLbl}>Diferencia</span>
                                                    <span style={{
                                                        ...styles.comparativaDiff,
                                                        color: diff === 0 ? '#48BB78' : diff > 0 ? '#63B3ED' : '#E53E3E',
                                                    }}>
                                                        {diff > 0 ? '+' : ''}{fmt(diff)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div>
                            <label style={styles.fieldLabel}>Observaciones de métodos de pago</label>
                            <textarea
                                value={observacionesMetodos}
                                onChange={e => !finalizado && setObservacionesMetodos(e.target.value)}
                                placeholder="Notas sobre los métodos de pago..."
                                disabled={finalizado}
                                rows={3}
                                style={{ ...styles.textarea, boxSizing: 'border-box' }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ── Desglose por producto: unidades vendidas ── */}
            {mostrarTablaVendidos && (
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-box me-2" style={{ color: '#F5C800' }}></i>
                        Desglose por producto (alimentos)
                    </div>
                    <div style={styles.tablaWrap}>
                        <table style={styles.tabla}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, minWidth: '120px' }}></th>
                                    {productosAlimentos.map(prod => (
                                        <th key={prod._id || prod.id} style={{ ...styles.th, textAlign: 'center' }}>
                                            {prod.nombre}
                                        </th>
                                    ))}
                                    <th style={{ ...styles.th, textAlign: 'center', color: '#F5C800' }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ ...styles.td, color: '#48BB78', fontWeight: 700 }}>Ventas</td>
                                    {ventasPorProducto.map((cantidad, ci) => (
                                        <td key={ci} style={{ ...styles.td, textAlign: 'center' }}>
                                            <span style={{ color: cantidad > 0 ? '#F0F0F0' : '#333', fontWeight: cantidad > 0 ? 700 : 400 }}>
                                                {cantidad > 0 ? cantidad : '—'}
                                            </span>
                                        </td>
                                    ))}
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        <span style={{ color: '#48BB78', fontWeight: 800 }}>{totalVentasFila > 0 ? totalVentasFila : '—'}</span>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ ...styles.td, color: '#63B3ED', fontWeight: 700 }}>Ventas ambulantes</td>
                                    {ambulantesPorProducto.map((cantidad, ci) => (
                                        <td key={ci} style={{ ...styles.td, textAlign: 'center' }}>
                                            <span style={{ color: cantidad > 0 ? '#F0F0F0' : '#333', fontWeight: cantidad > 0 ? 700 : 400 }}>
                                                {cantidad > 0 ? cantidad : '—'}
                                            </span>
                                        </td>
                                    ))}
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        <span style={{ color: '#63B3ED', fontWeight: 800 }}>{totalAmbulantesFila > 0 ? totalAmbulantesFila : '—'}</span>
                                    </td>
                                </tr>
                                <tr style={{ background: 'rgba(245,200,0,0.03)', borderTop: '1px solid #2E2E2E' }}>
                                    <td style={{ ...styles.td, color: '#C0C0C0', fontWeight: 700 }}>Vendidos</td>
                                    {vendidosPorProducto.map((cantidad, ci) => (
                                        <td key={ci} style={{ ...styles.td, textAlign: 'center' }}>
                                            <span style={{ color: cantidad > 0 ? '#F0F0F0' : '#333', fontWeight: cantidad > 0 ? 700 : 400 }}>
                                                {cantidad > 0 ? cantidad : '—'}
                                            </span>
                                        </td>
                                    ))}
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        <span style={{ color: '#F5C800', fontWeight: 800 }}>{totalVendidosGeneral}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Desglose por empleado ── */}
            {mostrarTablaEmpleados && (
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-users me-2" style={{ color: '#F5C800' }}></i>
                        Desglose por empleado (alimentos)
                    </div>
                    <div style={styles.tablaWrap}>
                        <table style={styles.tabla}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, minWidth: '150px' }}>Empleado</th>
                                    {productosAlimentos.map(prod => (
                                        <th key={prod._id || prod.id} style={{ ...styles.th, textAlign: 'center' }}>
                                            {prod.nombre}
                                        </th>
                                    ))}
                                    <th style={{ ...styles.th, textAlign: 'center', color: '#F5C800' }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matrizFilas.map((fila, idx) => (
                                    <tr key={fila.emp._id} style={{ background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                        <td style={styles.td}>
                                            <span style={{ color: '#C0C0C0', fontWeight: 600 }}>
                                                {`${fila.emp.nombre} ${fila.emp.apellido_paterno || ''}`.trim()}
                                            </span>
                                        </td>
                                        {fila.celdas.map((cantidad, ci) => (
                                            <td key={ci} style={{ ...styles.td, textAlign: 'center' }}>
                                                <span style={{ color: cantidad > 0 ? '#F0F0F0' : '#333', fontWeight: cantidad > 0 ? 700 : 400 }}>
                                                    {cantidad > 0 ? cantidad : '—'}
                                                </span>
                                            </td>
                                        ))}
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <span style={{ color: '#F5C800', fontWeight: 800 }}>{fila.total > 0 ? fila.total : '—'}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Conos: resumen + cierre ── */}
            {corteActivo && <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <i className="fas fa-ice-cream me-2" style={{ color: '#F5C800' }}></i>
                    Conos
                </div>
                <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {/* Indicador visual */}
                    <div style={styles.conosGrid}>
                        <div style={styles.conosFila}>
                            <span style={styles.conosLabel}>Iniciales</span>
                            <span style={styles.conosVal}>{conosInicialNum}</span>
                        </div>
                        <div style={styles.conosFila}>
                            <span style={styles.conosLabel}>
                                <i className="fas fa-plus me-1" style={{ fontSize: '0.75rem', color: '#48BB78' }}></i>
                                Ingresos del día
                            </span>
                            <span style={{ ...styles.conosVal, color: '#48BB78' }}>{totalConosIngresados}</span>
                        </div>
                        <div style={styles.conosFila}>
                            <span style={styles.conosLabel}>
                                <i className="fas fa-minus me-1" style={{ fontSize: '0.75rem', color: '#ED8936' }}></i>
                                Vendidos
                            </span>
                            <span style={{ ...styles.conosVal, color: '#ED8936' }}>{conosVendidosDisplay}</span>
                        </div>
                        <div style={{ ...styles.conosFila, borderTop: '1px solid #2E2E2E', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
                            <span style={{ ...styles.conosLabel, color: '#C0C0C0', fontWeight: 800 }}>Actuales</span>
                            <span style={{
                                ...styles.conosVal,
                                fontSize: '1.6rem',
                                color: conosActuales >= 0 ? '#F5C800' : '#E53E3E',
                            }}>
                                {conosActuales}
                            </span>
                        </div>
                    </div>

                    {/* Conos final + observaciones (editables si no finalizado) */}
                    <div style={styles.inicialesGrid}>
                        <div>
                            <label style={styles.fieldLabel}>Conos finales</label>
                            <CurrencyInput
                                value={conosFinal}
                                onValueChange={val => !finalizado && setConosFinal(val || '')}
                                decimalsLimit={0}
                                groupSeparator=","
                                decimalSeparator="."
                                placeholder="0"
                                disabled={finalizado}
                                onFocus={() => setFocusedConosFinal(true)}
                                onBlur={() => setFocusedConosFinal(false)}
                                style={currencyStyle(focusedConosFinal, finalizado)}
                            />
                        </div>
                        <div>
                            <label style={styles.fieldLabel}>Observaciones de conos</label>
                            <textarea
                                value={observacionesConos}
                                onChange={e => !finalizado && setObservacionesConos(e.target.value)}
                                placeholder="Notas sobre el conteo de conos..."
                                disabled={finalizado}
                                rows={3}
                                style={styles.textarea}
                            />
                        </div>
                    </div>
                </div>
            </div>}

            {/* ── Botones de acción ── */}
            {!pendiente && (
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                    {recienFinalizado && (
                        <button style={styles.btnTicket} onClick={() => setMostrarTicketCorte(true)}>
                            <i className="fas fa-print me-2"></i>
                            Imprimir ticket de corte
                        </button>
                    )}
                    <button
                        style={statusBtn
                            ? { ...styles.btnIniciar, opacity: 0.6, cursor: 'not-allowed', flex: 1 }
                            : { ...styles.btnIniciar, flex: 1 }}
                        onClick={handleIniciar}
                        disabled={statusBtn}
                    >
                        {statusBtn
                            ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Iniciando...</>
                            : <><i className="fas fa-play me-2"></i>Iniciar corte</>
                        }
                    </button>
                </div>
            )}

            {pendiente && (
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button
                        style={statusBtn ? { ...styles.btnGuardar, opacity: 0.6, cursor: 'not-allowed' } : styles.btnGuardar}
                        onClick={handleGuardar}
                        disabled={statusBtn}
                    >
                        {statusBtn
                            ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Guardando...</>
                            : <><i className="fas fa-save me-2"></i>Guardar</>
                        }
                    </button>
                    <button
                        style={statusBtn ? { ...styles.btnFinalizar, opacity: 0.6, cursor: 'not-allowed' } : styles.btnFinalizar}
                        onClick={handleFinalizar}
                        disabled={statusBtn}
                    >
                        {statusBtn
                            ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Finalizando...</>
                            : <><i className="fas fa-lock me-2"></i>Finalizar corte</>
                        }
                    </button>
                </div>
            )}

            {finalizado && (
                <div style={styles.finalizadoBanner}>
                    <i className="fas fa-check-circle me-2" style={{ color: '#48BB78', fontSize: '1.2rem' }}></i>
                    <span>
                        Corte finalizado el{' '}
                        {new Date(corteActivo.fecha_cierre).toLocaleDateString('es-MX')}{' '}
                        a las{' '}
                        {new Date(corteActivo.fecha_cierre).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                </div>
            )}

            {mostrarTicketCorte && recienFinalizado && corteActivo && (
                <TicketCorte
                    data={{
                        folio:           corteActivo.folio,
                        fecha:           corteActivo.fecha_inicio || corteActivo.fecha,
                        responsable:     `${empleadoLogueado?.nombre || ''} ${empleadoLogueado?.apellido_paterno || ''}`.trim(),
                        sucursal:        sucursalNombre,
                        efectivoInicial: corteActivo.total_inicial       || totalInicialf,
                        totalVentas:     corteActivo.total_ventas        || totalVentas,
                        totalVueltas:    corteActivo.total_vueltas       || totalVueltas,
                        totalGastos:     corteActivo.total_gastos        || totalGastos,
                        totalFinal:      corteActivo.total_final         || totalFinal,
                        metodos:         (corteActivo.desglose_metodos || desgloseMetodos).map(m => {
                            const totEntry = (corteActivo.totales || []).find(t => t.metodo == m._id);
                            const totalReal = totEntry?.total > 0 ? totEntry.total : null;
                            return { ...m, totalReal };
                        }),
                        conosVendidos:   corteActivo.total_conos_vendidos   || totalVendidosGeneral,
                        conosIniciales:  corteActivo.conos_inicial       || conosInicialNum,
                        conosTotales:    (corteActivo.conos_inicial || conosInicialNum) + (corteActivo.total_conos_ingresados || totalConosIngresados),
                        conosFinales:    corteActivo.conos_final         ?? parseInt(conosFinal) ?? 0,
                        tiposConos:      corteActivo.tipos_conos || productosAlimentos.map((prod, i) => ({
                            nombre:   prod.nombre,
                            vendidos: vendidosPorProducto[i] || 0,
                        })).filter(t => t.vendidos > 0),
                        evento:          eventoActivoRef?.nombre || corteActivo.evento_nombre || '',
                    }}
                    onImprimir={async () => {
                        await db.cortes.update(corteActivo.id, { ticket_impreso: true });
                    }}
                    onCerrar={() => {
                        setMostrarTicketCorte(false);
                        setRecienFinalizado(false);
                        cargarDatos();
                    }}
                />
            )}
        </div>
    );
};

const styles = {
    page: {
        maxWidth: '900px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
        paddingBottom: '20px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
        flexWrap: 'wrap',
        gap: '0.75rem',
    },
    headerTitulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1.2rem',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.5rem',
    },
    headerFecha: {
        color: '#888',
        fontSize: '0.88rem',
        marginTop: '0.25rem',
    },
    folioTag: {
        background: 'rgba(245,200,0,0.12)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '8px',
        padding: '0.15rem 0.65rem',
        fontSize: '0.85rem',
        fontFamily: 'monospace',
        fontWeight: 700,
    },
    statusTag: {
        borderRadius: '20px',
        padding: '0.2rem 0.7rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    statusPendiente: {
        background: 'rgba(229,62,62,0.1)',
        color: '#E53E3E',
        border: '1px solid rgba(229,62,62,0.3)',
    },
    statusFinalizado: {
        background: 'rgba(72,187,120,0.1)',
        color: '#48BB78',
        border: '1px solid rgba(72,187,120,0.3)',
    },
    responsableBadge: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.5rem 1rem',
        display: 'flex',
        alignItems: 'center',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
    },
    cardHeader: {
        background: '#111',
        borderBottom: '1px solid #2E2E2E',
        padding: '0.85rem 1.25rem',
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '0.95rem',
        display: 'flex',
        alignItems: 'center',
    },
    inicialesGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1.25rem',
    },
    totalInicialWrap: {
        padding: '1.25rem',
    },
    fieldLabel: {
        display: 'block',
        color: '#666',
        fontSize: '0.78rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '0.5rem',
    },
    textarea: {
        background: '#1F1F1F',
        border: '2px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.75rem 1rem',
        color: '#F0F0F0',
        fontSize: '0.95rem',
        width: '100%',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
    },
    conosGrid: {
        background: '#111',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '1rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.6rem',
    },
    conosFila: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    conosLabel: {
        color: '#666',
        fontSize: '0.85rem',
        fontWeight: 600,
    },
    conosVal: {
        color: '#C0C0C0',
        fontWeight: 800,
        fontSize: '1.15rem',
    },
    hint: {
        color: '#555',
        fontSize: '0.8rem',
        marginTop: '0.5rem',
        marginBottom: 0,
    },
    rubrosGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
    },
    rubroCard: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        padding: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.35rem',
    },
    rubroLabel: {
        color: '#888',
        fontSize: '0.82rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'flex',
        alignItems: 'center',
    },
    rubroTotal: {
        fontWeight: 800,
        fontSize: '1.5rem',
        lineHeight: 1.1,
    },
    rubroSub: {
        color: '#555',
        fontSize: '0.8rem',
    },
    comparativaWrap: {
        background: '#111',
        border: '1px solid #2E2E2E',
        borderRadius: '8px',
        padding: '0.5rem 0.75rem',
        marginTop: '0.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.15rem',
    },
    comparativaFila: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    comparativaLbl: {
        color: '#555',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
    },
    comparativaVal: {
        color: '#888',
        fontSize: '0.82rem',
        fontWeight: 700,
        fontFamily: 'monospace',
    },
    comparativaDiff: {
        fontSize: '0.85rem',
        fontWeight: 800,
        fontFamily: 'monospace',
    },
    tablaWrap: { overflowX: 'auto' },
    tabla: { width: '100%', borderCollapse: 'collapse' },
    th: {
        color: '#888',
        fontSize: '0.78rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '0.75rem 1.25rem',
        textAlign: 'left',
        borderBottom: '1px solid #2E2E2E',
        background: '#111',
    },
    td: {
        padding: '0.85rem 1.25rem',
        borderBottom: '1px solid #1E1E1E',
    },
    btnIniciar: {
        background: '#2D3748',
        color: '#F0F0F0',
        border: '1px solid #4A5568',
        borderRadius: '12px',
        padding: '1rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        width: '100%',
        cursor: 'pointer',
    },
    btnGuardar: {
        flex: 1,
        background: 'transparent',
        color: '#C0C0C0',
        border: '1px solid #2E2E2E',
        borderRadius: '12px',
        padding: '1rem',
        fontWeight: 700,
        fontSize: '1rem',
        cursor: 'pointer',
    },
    btnFinalizar: {
        flex: 2,
        background: '#F5C800',
        color: '#111',
        border: 'none',
        borderRadius: '12px',
        padding: '1rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        cursor: 'pointer',
    },
    btnTicket: {
        background: '#2F855A',
        color: '#F0F0F0',
        border: '1px solid #38A169',
        borderRadius: '12px',
        padding: '0.75rem 1.25rem',
        fontWeight: 700,
        fontSize: '0.95rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
    },
    finalizadoBanner: {
        background: 'rgba(72,187,120,0.08)',
        border: '1px solid rgba(72,187,120,0.25)',
        borderRadius: '12px',
        padding: '1rem 1.25rem',
        color: '#C0C0C0',
        fontSize: '0.95rem',
        display: 'flex',
        alignItems: 'center',
    },
};

export default Corte;
